import React from 'react';
import { Box, Typography } from '@mui/material';
import { IFlightServiceLeg } from 'features/salesOrder/types';
import { estimateItineraryEmissions } from '../helpers/flightCo2';

interface FlightCo2SummaryProps {
	flightLegs: IFlightServiceLeg[];
}

/**
 * Formatea un número con separadores de miles y decimales
 */
const formatNumber = (value: number, decimals = 0): string => {
	return value.toLocaleString('es-UY', {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
};

/**
 * Formatea el valor de CO₂ mostrando kg o toneladas según corresponda
 */
const formatCo2 = (co2Kg: number): string => {
	if (co2Kg >= 1000) {
		const tons = co2Kg / 1000;
		return `${formatNumber(tons, 2)} t CO₂e`;
	}
	return `${formatNumber(Math.round(co2Kg))} kg CO₂e`;
};

export const FlightCo2Summary = ({ flightLegs }: FlightCo2SummaryProps): JSX.Element | null => {
	const emissions = estimateItineraryEmissions(flightLegs);

	// Si no se puede calcular, no renderizar nada
	if (!emissions) {
		return null;
	}

	const { totalDistanceKm, economyCo2Kg, businessCo2Kg } = emissions;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				mt: 1.5,
				alignSelf: 'flex-end',
				alignItems: 'flex-end',
				gap: 0.5,
			}}
		>
			<Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
				Emisión de CO₂ estimada por pasajero
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
				<span>🌿</span>
				<span>~{formatNumber(Math.round(totalDistanceKm))} km</span>
				<span>·</span>
				<span>Economy: {formatCo2(economyCo2Kg)}</span>
				<span>·</span>
				<span>Business: {formatCo2(businessCo2Kg)}</span>
			</Typography>
			<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'right', maxWidth: '100%' }}>
				Cálculo aproximado basado en distancias entre aeropuertos y factores promedio de emisión por km de vuelo. No
				representa un dato exacto del vuelo operado.
			</Typography>
			<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'right' }}>
				Cada viaje tiene impacto. Cuidemos el planeta entre todos 🌎
			</Typography>
		</Box>
	);
};
